<template>
    <div class="l-goal-list-item" data-test-class="goal-item">
        <div class="tw-flex tw-space-x-6 tw-items-center tw-justify-center">
            <div class="tw-flex-grow-0 tw-p-2">
                <l-profile-picture
                    v-if="goal.type === 'goalUser' && goal.user && goal.user.picture"
                    :user="goal.user"
                    size="xm"
                />
                <img
                    v-else-if="goal.icon"
                    :src="goal.icon"
                    class="img-circle user-pic-xm"
                    alt="Goal Icon"
                />
            </div>

            <div class="tw-flex-1">
                <div class="fake-link ellipse-text">
                    <a
                        class="fake-link"
                        :href="`/app/#/goals/details/${goal._id}`"
                        @click.prevent="
                            () => {
                                $router.push(`/goals/details/${goal._id}`);
                            }
                        "
                    >
                        {{ goal.name }}
                    </a>
                </div>
                <div class="tw-grid tw-gap-6 tw-grid-cols-12">
                    <div class="tw-col-span-full md:tw-col-span-5">
                        <div
                            class="tw-flex tw-space-x-2 tw-justify-start tw-items-start tw-text-gray-500 tw-truncate"
                        >
                            <span
                                v-if="hasKeyResults"
                                class="clickable tw-select-none"
                                tabindex="0"
                                @click="toggleExpandKR()"
                                @keyup.enter="toggleExpandKR()"
                            >
                                <span>{{
                                    $t(`goals.list.labelKRs`, {
                                        count: goal.keyResults.length,
                                    })
                                }}</span>
                                <i
                                    class="fal fa-fw tw-ml-2"
                                    :class="[expandKR ? 'fa-caret-down' : 'fa-caret-right']"
                                ></i>
                            </span>
                            <span
                                v-if="goal.dev"
                                v-b-popover.hover.top="{
                                    content: $t(`goals.update.labelDevelopmentGoal`),
                                }"
                            >
                                <i class="fal fa-seedling color-success"></i>
                            </span>
                            <span>
                                <span> {{ goal.contributionTag }} </span>
                            </span>
                        </div>
                    </div>

                    <div
                        class="tw-hidden md:tw-block md:tw-col-span-3 ellipse-text tw-text-gray-500"
                    >
                        <span v-if="goal.tags">
                            <i class="fal fa-tag tw-mr-1"></i>
                            {{ goal.tags }}
                        </span>

                        <span v-if="goal.metricWeight"> W{{ goal.metricWeight }}% </span>
                    </div>

                    <div
                        class="tw-hidden md:tw-block md:tw-col-span-4 xl:tw-col-span-2 tw-text-gray-500"
                    >
                        <div class="tw-flex tw-space-x-6">
                            <template>
                                <span
                                    v-if="goal.deadline"
                                    v-b-popover.hover.top="{
                                        content: `Deadline`,
                                    }"
                                    class="tw-flex-1 tw-truncate"
                                >
                                    {{ goal.deadline | moment("MMM D") }}
                                </span>
                                <span v-else class="tw-flex-1"></span>
                            </template>

                            <template>
                                <span
                                    v-if="goal.toDiscuss === 'canceled'"
                                    v-b-popover.hover.top="{
                                        content: $t(`goals.list.helpCanceled`),
                                    }"
                                    class="color-danger"
                                >
                                    <i class="fal fa-stop-circle"></i>
                                </span>
                                <span
                                    v-else-if="goal.toDiscuss === 'offTrack'"
                                    v-b-popover.hover.top="{
                                        content: $t(`goals.list.helpActiveOffTrack`),
                                    }"
                                    class="color-danger"
                                >
                                    <i class="fal fa-times"></i>
                                </span>
                                <span
                                    v-else-if="goal.toDiscuss === 'inputNeeded'"
                                    v-b-popover.hover.top="{
                                        content: $t(`goals.list.helpActiveInputNeeded`),
                                    }"
                                    class="color-warning"
                                >
                                    <i class="fal fa-exclamation-triangle"></i>
                                </span>
                                <span
                                    v-else-if="goal.toDiscuss === 'onTrack'"
                                    v-b-popover.hover.top="{
                                        content: $t(`goals.list.helpActiveOnTrack`),
                                    }"
                                    class="color-success"
                                >
                                    <i class="fal fa-check"></i>
                                </span>
                                <span
                                    v-else-if="goal.toDiscuss === 'done'"
                                    v-b-popover.hover.top="{
                                        content: $t(`goals.list.helpDone`),
                                    }"
                                    class="color-success"
                                >
                                    <i class="fal fa-flag"></i>
                                </span>
                            </template>

                            <span>{{ goal.progress || 0 }}%</span>
                        </div>
                    </div>

                    <div class="tw-hidden xl:tw-block md:tw-col-span-2">
                        <div style="margin-top: 10px; margin-bottom: 0px">
                            <l-progress
                                :progress-percent="goal.progress || 0"
                                :progress-bar-class="
                                    goal.progress <= 33
                                        ? 'progress-bar-danger'
                                        : goal.progress > 33 && goal.progress <= 66
                                        ? 'progress-bar-warning'
                                        : goal.progress > 66
                                        ? 'progress-bar-success'
                                        : ''
                                "
                                size="xs"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="expandKR && hasKeyResults"
            class="tw-mt-4 tw-border-0 tw-border-t tw-border-b tw-border-gray-200 tw-border-solid"
        >
            <div
                v-for="(keyResult, index) in goal.keyResults"
                :key="keyResult._id"
                @keyup.enter="openEditKRModal(goal, keyResult._id)"
                @click="openEditKRModal(goal, keyResult._id)"
            >
                <div
                    class="tw-flex tw-space-x-6 tw-items-center tw-bg-gray-50 tw-border-0 tw-border-white tw-border-solid"
                    :class="{ 'tw-border-b-2': index !== goal.keyResults.length - 1 }"
                >
                    <div class="tw-flex-grow-0 tw-p-2">
                        <l-profile-picture
                            v-if="keyResult.owner"
                            :user="keyResult.owner"
                            size="xm"
                        />
                        <img
                            v-else-if="goal.icon"
                            :src="goal.icon"
                            class="img-circle user-pic-xm"
                            alt="Goal Icon"
                        />
                    </div>
                    <div class="tw-flex-1">
                        <div class="tw-grid tw-gap-6 tw-grid-cols-12">
                            <div
                                class="tw-truncate tw-col-span-8"
                                :class="{
                                    'fake-link clickable': keyResult.canUpdateKR,
                                }"
                            >
                                <i
                                    v-if="keyResult.type == 'keyResult'"
                                    v-b-popover.hover.top="$t('goals.update.labelKeyResult')"
                                    class="fal fa-flag tw-text-gray-500 tw-mr-4"
                                ></i>
                                <i
                                    v-else
                                    v-b-popover.hover.top="$t('goals.update.labelInitiative')"
                                    class="fal fa-sticky-note tw-text-gray-500 tw-mr-4"
                                ></i>
                                <span v-b-popover.hover.top="keyResult.name">{{
                                    keyResult.name
                                }}</span>
                            </div>
                            <div
                                class="tw-col-span-1"
                                :class="{ 'fake-link clickable': keyResult.canUpdateKR }"
                            >
                                <span>{{ keyResult.progress }}% </span>
                            </div>
                            <div class="tw-col-span-1 tw-text-center">
                                <span
                                    v-if="keyResult.toDiscuss === 'canceled'"
                                    v-b-popover.hover.top="{
                                        content: $t(`goals.list.helpCanceled`),
                                    }"
                                    class="color-danger"
                                >
                                    <i class="fal fa-stop-circle"></i>
                                </span>
                                <span
                                    v-else-if="keyResult.toDiscuss === 'offTrack'"
                                    v-b-popover.hover.top="{
                                        content: $t(`goals.list.helpActiveOffTrack`),
                                    }"
                                    class="color-danger"
                                >
                                    <i class="fal fa-times"></i>
                                </span>
                                <span
                                    v-else-if="keyResult.toDiscuss === 'inputNeeded'"
                                    v-b-popover.hover.top="{
                                        content: $t(`goals.list.helpActiveInputNeeded`),
                                    }"
                                    class="color-warning"
                                >
                                    <i class="fal fa-exclamation-triangle"></i>
                                </span>
                                <span
                                    v-else-if="keyResult.toDiscuss === 'onTrack'"
                                    v-b-popover.hover.top="{
                                        content: $t(`goals.list.helpActiveOnTrack`),
                                    }"
                                    class="color-success"
                                >
                                    <i class="fal fa-check"></i>
                                </span>
                                <span
                                    v-else-if="keyResult.toDiscuss === 'done'"
                                    v-b-popover.hover.top="{
                                        content: $t(`goals.list.helpDone`),
                                    }"
                                    class="color-success"
                                >
                                    <i class="fal fa-flag"></i>
                                </span>
                            </div>
                            <div
                                class="tw-col-span-2 tw-truncate"
                                :class="{ 'fake-link clickable': keyResult.canUpdateKR }"
                            >
                                <span v-if="keyResult.metricType !== 'none'">
                                    {{ keyResult.metricCurrent }} /
                                    {{ keyResult.metricEnd }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import LProfilePicture from "./LProfilePicture";
import LProgress from "./LProgress";

export default {
    components: {
        LProfilePicture,
        LProgress,
    },
    props: {
        goal: {
            type: Object,
            required: true,
        },
        expandKeyResults: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            expandKR: false,
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
        }),
        hasKeyResults() {
            return Array.isArray(this.goal?.keyResults) && this.goal.keyResults.length;
        },
    },
    watch: {
        expandKeyResults: {
            immediate: true,
            handler(newValue) {
                // adjust value based on incoming property from parent
                this.expandKR = newValue;
            },
        },
    },
    methods: {
        toggleExpandKR() {
            this.expandKR = !this.expandKR;
        },
        openEditKRModal(goal, id) {
            const keyResult = this.goal?.keyResults.find(kr => kr._id === id) || {};
            if (!keyResult.canUpdateKR) {
                return;
            }
            this.$router.replace({
                query: { ...this.$route.query, editGoalId: goal._id, editKeyResultId: id },
            });
        },
    },
};
</script>

<style scoped></style>
